// Generic localStorage draft store behind the admin "תצוגה מקדימה" flow.
//
// Extracted verbatim from the homepage's useSiteContent implementation so every
// revisioned content document (homepage, AI Mentions, ...) shares one envelope
// format, one TTL policy and one purge pass - the only per-document parts are
// the key prefix, the contentType tag and the strict values parser.

export const CONTENT_DRAFT_SCHEMA_VERSION = 1;
export const CONTENT_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
// A draft stamped further ahead than this is a broken/forged clock, not skew.
export const CONTENT_DRAFT_MAX_SKEW_MS = 2 * 60 * 1000;

export interface ContentDraft<T> {
    schemaVersion: number;
    // Which content document this draft belongs to. Older envelopes predate the
    // field; they are only reachable under their own prefix anyway (see read).
    contentType?: string;
    createdAt: string;
    values: T;
}

export interface ContentDraftStore<T> {
    prefix: string;
    contentType: string;
    key: (token: string) => string;
    // Returns the draft values, or null when the key is missing / unparsable /
    // expired / belongs to a different content type.
    read: (token: string) => T | null;
    write: (token: string, values: T) => boolean;
    // Drops every stale/expired draft key under this prefix, so previews never
    // pile up in localStorage.
    purgeExpired: () => void;
}

export const createContentDraftStore = <T>(
    contentType: string,
    prefix: string,
    parseValues: (input: unknown) => T | null,
): ContentDraftStore<T> => {
    const key = (token: string) => `${prefix}${token}`;

    const parseDraft = (raw: string | null): ContentDraft<T> | null => {
        if (!raw) {
            return null;
        }

        let envelope: any;
        try {
            envelope = JSON.parse(raw);
        } catch (error) {
            return null;
        }

        if (!envelope || typeof envelope !== 'object') {
            return null;
        }
        if (envelope.schemaVersion !== CONTENT_DRAFT_SCHEMA_VERSION) {
            return null;
        }
        // A draft written for another content document is never readable here,
        // whatever prefix it was found under.
        if (envelope.contentType !== undefined && envelope.contentType !== contentType) {
            return null;
        }

        const createdAt = Date.parse(envelope.createdAt);
        const age = Date.now() - createdAt;
        // Too old, or stamped in the future by more than plausible clock skew
        if (Number.isNaN(createdAt) || age > CONTENT_DRAFT_TTL_MS || age < -CONTENT_DRAFT_MAX_SKEW_MS) {
            return null;
        }

        // A draft never passed through the backend's validators, so the values
        // are validated strictly and rejected WHOLE - a half-valid draft is not
        // shown.
        const values = parseValues(envelope.values);
        if (!values) {
            return null;
        }

        return {
            schemaVersion: envelope.schemaVersion,
            contentType: envelope.contentType,
            createdAt: envelope.createdAt,
            values,
        };
    };

    const read = (token: string): T | null => {
        let raw: string | null;
        try {
            raw = window.localStorage.getItem(key(token));
        } catch (error) {
            return null;
        }

        return parseDraft(raw)?.values ?? null;
    };

    const write = (token: string, values: T): boolean => {
        const envelope: ContentDraft<T> = {
            schemaVersion: CONTENT_DRAFT_SCHEMA_VERSION,
            contentType,
            createdAt: new Date().toISOString(),
            values,
        };

        try {
            window.localStorage.setItem(key(token), JSON.stringify(envelope));
            return true;
        } catch (error) {
            return false;
        }
    };

    const purgeExpired = () => {
        try {
            const stale: string[] = [];
            for (let i = 0; i < window.localStorage.length; i++) {
                const storageKey = window.localStorage.key(i);
                if (storageKey && storageKey.startsWith(prefix)
                    && !parseDraft(window.localStorage.getItem(storageKey))) {
                    stale.push(storageKey);
                }
            }
            stale.forEach(storageKey => window.localStorage.removeItem(storageKey));
        } catch (error) {
            // localStorage unavailable (private mode / disabled) - nothing to purge
        }
    };

    return {prefix, contentType, key, read, write, purgeExpired};
};
